import { useQuery, useMutation, useQueryClient } from 'react-query';
import { predictionManagerAxiosApi } from 'src/Config/axiosApi';

// ── Types — mirror the backend event_agent_runs doc + output contract ──

export interface ProgressEvent {
  ts: string;
  phase: string;
  message: string;
}

export interface AgentRunResult {
  status: 'completed' | 'partial' | 'failed';
  event?: {
    title?: string;
    kalshiEventTickers?: string[];
    polymarketEventSlug?: string;
    polymarketEventId?: string;
  };
  cachedMarketsUpserted?: { polymarket: number; kalshi: number };
  pairsCreated?: Array<{
    pairId?: string;
    name?: string;
    polymarketExternalId?: string;
    kalshiTicker?: string;
    marketType?: string;
    polarityInverted?: boolean;
    confidence?: number;
  }>;
  pairsSkipped?: Array<{
    polymarketExternalId?: string;
    kalshiTicker?: string;
    reason?: string;
  }>;
  unmatchedMarkets?: {
    polymarket?: Array<{
      externalId: string;
      question?: string;
      sportsMarketType?: string;
      reason: string;
    }>;
    kalshi?: Array<{ ticker: string; title?: string; reason: string }>;
  };
  gaps?: {
    aliases?: Array<Record<string, unknown>>;
    shapes?: Array<{ description?: string; affectedMarketCount?: number; prUrl?: string | null }>;
  };
  warnings?: string[];
}

export interface EventAgentRun {
  _id: string;
  eventQuery: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  jobName?: string;
  progressLog?: ProgressEvent[];
  result: AgentRunResult | null;
  feedback?: Array<{ ts: string; missedPairs: unknown[]; status: string }>;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

interface RunsResponse {
  runs: EventAgentRun[];
  total: number;
}

// ── Hooks ──────────────────────────────────────────────────────────────

/** Start a /map-event agent run. */
export function useStartEventAgentRun() {
  const qc = useQueryClient();
  return useMutation(
    async (eventQuery: string) => {
      const { data } = await predictionManagerAxiosApi.post<EventAgentRun>(
        '/event-agent/runs',
        { eventQuery },
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('event-agent-runs');
      },
    },
  );
}

/**
 * Poll one run. Refetches every 2.5s while the run is queued/running, then
 * stops once it reaches a terminal state.
 */
export function useEventAgentRun(id: string | null) {
  return useQuery(
    ['event-agent-run', id],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<EventAgentRun>(
        `/event-agent/runs/${id}`,
      );
      return data;
    },
    {
      enabled: !!id,
      refetchInterval: (data) =>
        data && (data.status === 'queued' || data.status === 'running')
          ? 2_500
          : false,
    },
  );
}

/** List recent runs (newest first). */
export function useEventAgentRuns(limit = 20) {
  return useQuery(
    ['event-agent-runs', limit],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<RunsResponse>(
        '/event-agent/runs',
        { params: { limit } },
      );
      return data;
    },
    { keepPreviousData: true },
  );
}

export interface MissedPair {
  polymarketExternalId: string;
  kalshiTicker: string;
  polarityInverted?: boolean;
}

/** Submit operator corrections — pairs the agent missed. */
export function useSubmitEventAgentFeedback() {
  const qc = useQueryClient();
  return useMutation(
    async ({ runId, missedPairs }: { runId: string; missedPairs: MissedPair[] }) => {
      const { data } = await predictionManagerAxiosApi.post<EventAgentRun>(
        `/event-agent/runs/${runId}/feedback`,
        { missedPairs },
      );
      return data;
    },
    {
      onSuccess: (_data, vars) => {
        qc.invalidateQueries(['event-agent-run', vars.runId]);
      },
    },
  );
}
